import {
    VuexModule,
    Module,
    MutationAction,
    Mutation,
    Action,
    getModule,
} from 'vuex-module-decorators'
import {
    login,
    logout,
    getInfo,
    getToken,
    setToken,
    removeToken,
    verifyToken,
    getUserOperations,
} from '@/api/login'
import store from '@/store'
import { HTTP } from '@/api/http-constants'
import { ApiService } from '@/api/api.service'
import { getOperationID } from '@/api/operations'

export interface IUserState {
    token: string
    userInfos: {
        username
        nickname
        firstname
        lastname
        id?: number
        imagePath: string
    }
}

@Module({ dynamic: true, store, name: 'user' })
class User extends VuexModule implements IUserState {
    @Mutation
    public SET_TOKEN(token: string) {
        this.token = token
    }
    public token: string = ''
    public userInfos = {
        username: '',
        nickname: '',
        firstname: '',
        lastname: '',
        number: null,
        id: null,
        imagePath: null,
    }
    foyerCompleted: boolean | null = null
    appartCompleted: boolean | null = null

    @Mutation
    public SET_USERINFOS(userInfos) {
        this.userInfos = {
            ...this.userInfos,
            ...userInfos,
        }
    }

    @Mutation
    public SET_AVATAR(imagePath) {
        this.userInfos.imagePath = imagePath
    }

    @Mutation
    public SET_NICKNAME(nickname) {
        this.userInfos.nickname = nickname
    }

    @Mutation
    public SET_FOYER_COMPLETED(foyerCompleted: boolean) {
        this.foyerCompleted = foyerCompleted
    }

    @Mutation
    public SET_APPART_COMPLETED(appartCompleted: boolean) {
        this.appartCompleted = appartCompleted
    }

    @Action({ rawError: true })
    public async Init() {
        if (this.userInfos.id !== null) {
            return this.userInfos
        }

        console.log('init')

        const apiService = new ApiService()
        const token = getToken()
        const tokenDecode = verifyToken(token)
        if (tokenDecode.success) {
            this.context.commit('SET_TOKEN', token)
            this.context.commit('SET_USERINFOS', tokenDecode.data)
            this.context.commit('SET_AVATAR', null)

            const resp = await apiService.getBuyer(this.userInfos.id)
            const buyer = resp.data
            this.context.commit('SET_AVATAR', buyer.imagePath)
        }

        await this.checkRequiredInfos()
    }

    @Action({ rawError: true })
    private async checkRequiredInfos() {
        if (this.userInfos.id === null) {
            return
        }

        const opeID = await getOperationID()
        if (opeID === -1) {
            return
        }

        // On check si la page foyer et la page appart ont été remplies
        const apiService = new ApiService()
        const userOpeResp = await apiService.getUserOperation(
            this.userInfos.id,
            opeID
        )
        const userOpe = userOpeResp.data

        /**
         * On considére la page foyer complétée si le comment est différent de
         * null et la page appart complétée si la surface est différente de null
         */
        const foyerCompleted = userOpe.sharedSpaces.length > 0
        const appartCompleted =
            userOpe.wishedSurface !== null && userOpe.favorite !== null

        this.context.commit('SET_FOYER_COMPLETED', foyerCompleted)
        this.context.commit('SET_APPART_COMPLETED', appartCompleted)
    }

    @Action
    public markFoyerAsCompleted() {
        this.context.commit('SET_FOYER_COMPLETED', true)
    }

    @Action
    public markAppartAsCompleted() {
        this.context.commit('SET_APPART_COMPLETED', true)
    }

    @Action({ rawError: true })
    public async Login(userInfo: { username: string; password: string }) {
        const username = userInfo.username.trim()
        try {
            const response = await login(username, userInfo.password)
            const data = response.data
            const tokenDecode = verifyToken(data.token)
            if (tokenDecode.success) {
                setToken(data.token)
                HTTP.defaults.headers['Authorization'] =
                    'Bearer ' + data.token
                this.context.commit('SET_TOKEN', data.token)
                this.context.commit('SET_USERINFOS', tokenDecode.data)
                return true
            } else {
                throw new Error(tokenDecode.message)
            }
        } catch (e) {
            throw new Error('erreur de login')
        }
    }

    @Action({ rawError: true })
    public async SetToken(token) {
        const tokenDecode = verifyToken(token)
        if (tokenDecode.success) {
            setToken(token)
            HTTP.defaults.headers['Authorization'] = 'Bearer ' + token
            this.context.commit('SET_TOKEN', token)
            this.context.commit('SET_USERINFOS', tokenDecode.data)
            return this.userInfos
        } else {
            return tokenDecode.message
        }
    }

    @Action({ rawError: true })
    public async FedLogOut() {
        removeToken()
        return ''
    }

    @MutationAction({ mutate: ['roles', 'name', 'avatar'] })
    public async GetInfo() {
        const token = getToken()
        if (token === undefined) {
            throw Error('GetInfo: token is undefined!')
        }
        const { data } = await getInfo(token)
        if (data.roles && data.roles.length > 0) {
            return {
                roles: data.roles,
                name: data.name,
                avatar: data.avatar,
            }
        } else {
            throw Error('GetInfo: roles must be a non-null array!')
        }
    }

    @Action({ rawError: true })
    public async LogOut() {
        if (getToken() === undefined) {
            throw Error('LogOut: token is undefined!')
        }
        this.context.commit('SET_USERINFOS', {
            username: '',
            nickname: '',
            firstname: '',
            lastname: '',
            id: null,
        })
        this.context.commit('SET_TOKEN', '')
        console.log('logout')
        await logout()
        return true
    }

    @Action({ commit: 'SET_AVATAR' })
    public async SetAvatar(imagePath) {
        return imagePath
    }

    @Action({ commit: 'SET_NICKNAME' })
    public async SetNickname(nickname) {
        return nickname
    }
}

export const UserModule = getModule(User)
